import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { ButtonBase, makeStyles, Theme, createStyles } from '@material-ui/core';

import { AIChatContext } from '../../context';
import { LailaCardsContext } from '../cards/LailaCardsContext';
import { LailaOrb } from './LailaOrb';
import { LailaPanel } from './LailaPanel';

export const LAILA_PAGE = '/vyce-ai';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const SHORTCUT_LABEL = isMac ? '⌘J' : 'Ctrl J';

interface Props {
  // On the Vyce AI page the chat is already full screen.
  hidden?: boolean;
}

// The orb in the corner of every page and the panel it opens. ⌘J / Ctrl+J
// toggles it; replies that arrive while it's closed show as a badge.
export const LailaLauncher: React.FC<Props> = ({ hidden = false }) => {
  const classes = useStyles();
  const history = useHistory();
  const { messages, thinking, activateAIChat } = useContext(AIChatContext);
  const outerCardActions = useContext(LailaCardsContext);
  // A card taking the user to a page (a full form) closes the panel, so it doesn't cover it
  const cardActions = useMemo(
    () => ({
      ...outerCardActions,
      openPage: (path: string) => {
        setOpen(false);
        history.push(path);
      },
    }),
    [outerCardActions, history]
  );
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const seen = useRef(messages.length);
  // Read by the message counter, which should run on new messages only.
  const outOfSight = useRef(true);
  outOfSight.current = !open && !hidden;

  const show = useCallback(() => {
    activateAIChat();
    setUnread(0);
    setOpen(true);
  }, [activateAIChat]);

  // Count Laila's replies that land while she's out of sight.
  useEffect(() => {
    const added = messages.slice(seen.current);
    seen.current = messages.length;
    if (outOfSight.current) {
      setUnread(value => value + added.filter(message => message.role === 'assistant').length);
    }
  }, [messages]);

  useEffect(() => {
    if (hidden) {
      setOpen(false);
      setUnread(0);
    }
  }, [hidden]);

  useEffect(() => {
    if (hidden) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        open ? setOpen(false) : show();
      } else if (event.key === 'Escape' && open && !event.defaultPrevented) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hidden, open, show]);

  if (hidden) {
    return null;
  }

  return (
    <>
      {open && (
        <LailaCardsContext.Provider value={cardActions}>
          <LailaPanel
            onClose={() => setOpen(false)}
            onExpand={() => {
              setOpen(false);
              history.push(LAILA_PAGE);
            }}
          />
        </LailaCardsContext.Provider>
      )}

      <ButtonBase
        className={clsx(classes.pill, { [classes.pillHidden]: open })}
        onClick={show}
        aria-label={`Ask Laila (${SHORTCUT_LABEL})`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <LailaOrb size={52} halo={false} unread={unread} state={thinking ? 'thinking' : 'idle'} />
        <span className={classes.label}>
          Ask Laila
          <kbd className={classes.kbd}>{SHORTCUT_LABEL}</kbd>
        </span>
      </ButtonBase>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pill: {
      position: 'fixed',
      right: 24,
      bottom: 24,
      zIndex: theme.zIndex.drawer,
      display: 'flex',
      alignItems: 'center',
      height: 60,
      padding: 4,
      borderRadius: 30,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[8],
      transition: 'opacity 150ms, transform 150ms',
      // Hover or keyboard focus reveals the label; the orb's badge stays unclipped.
      '@media (hover: hover)': {
        '&:hover $label, &.Mui-focusVisible $label': {
          maxWidth: 160,
          opacity: 1,
          paddingLeft: 10,
          paddingRight: 12,
        },
      },
      '@media (prefers-reduced-motion: reduce)': { transition: 'none', '& $label': { transition: 'none' } },
      [theme.breakpoints.down('xs')]: { right: 16, bottom: 16 },
    },
    pillHidden: { opacity: 0, transform: 'scale(.8)', pointerEvents: 'none' },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      maxWidth: 0,
      paddingLeft: 0,
      paddingRight: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontSize: 15,
      fontWeight: 600,
      color: theme.palette.text.primary,
      opacity: 0,
      transition:
        'max-width 400ms cubic-bezier(.2,.8,.2,1), padding 400ms cubic-bezier(.2,.8,.2,1), opacity 250ms',
    },
    kbd: {
      fontFamily: 'inherit',
      fontSize: 12,
      fontWeight: 500,
      padding: '1px 6px',
      borderRadius: 6,
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.divider}`,
    },
  })
);
